import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MdSearch, MdInfoOutline, MdEdit, MdDelete } from 'react-icons/md';

const API_GUIDE_STEPS = [
  {
    color: '#f59e0b',
    title: 'Create or Import APIs',
    desc: 'Click "Add New API" to manually define an endpoint, or use "Upload Postman Collection" to bulk-import from an exported Postman JSON file.',
  },
  {
    color: '#fbbf24',
    title: 'Set Method & URL',
    desc: 'Choose GET / POST / PUT / PATCH / DELETE and enter a relative path like /v1/users (HOST is prepended automatically) or a full URL.',
  },
  {
    color: '#d97706',
    title: 'Use {{variable}} Placeholders',
    desc: 'In the URL path, headers, or request body, wrap any dynamic value in double curly braces: /v1/users/{{userId}} or {"name": "{{name}}"}.',
  },
  {
    color: '#64748b',
    title: 'Set Expected Status Code',
    desc: 'Enter the HTTP status you expect on success (e.g., 200, 201). The test step will PASS only if the response matches this code.',
  },
];

function APIQuickStartGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" style={{ marginBottom: '16px', padding: '14px 18px', border: '1px solid var(--border)' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
      >
        <MdInfoOutline color="var(--primary)" size={18} />
        <strong style={{ fontSize: '14px' }}>API Collection — Quick Start Guide</strong>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-muted)' }}>{open ? '▲ Hide' : '▼ Show'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
          {API_GUIDE_STEPS.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: '12px', alignItems: 'flex-start',
              background: 'var(--bg-input)', borderLeft: `4px solid ${s.color}`,
              borderRadius: '8px', padding: '12px',
            }}>
              <div style={{
                minWidth: '24px', height: '24px', borderRadius: '50%',
                backgroundColor: s.color, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '12px', flexShrink: 0,
              }}>{i + 1}</div>
              <div>
                <strong style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>{s.title}</strong>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>{s.desc}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function APIManagement({ onStats, host }) {
  const [apis, setApis] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [importing, setImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedPostmanFile, setSelectedPostmanFile] = useState(null);
  const [apiToDelete, setApiToDelete] = useState(null);
  const [deletingApi, setDeletingApi] = useState(false);
  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    method: 'GET',
    headers: '{"Content-Type": "application/json"}',
    bodyMode: 'raw',
    body: '',
    formdata: [],
    expectedStatus: 200
  });


  useEffect(() => {
    fetchApis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  const fetchApis = async () => {
    try {
      const response = await axios.get('/api/apis');
      setApis(response.data);
      onStats();
    } catch (error) {
      setError('Failed to fetch APIs: ' + error.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      let headers = {};
      try {
        headers = JSON.parse(formData.headers);
      } catch (e) {
        setError('Invalid JSON in headers');
        return;
      }

      const apiData = {
        ...formData,
        headers,
        body: formData.bodyMode === 'formdata' 
          ? { mode: 'formdata', formdata: formData.formdata }
          : formData.body
      };


      if (editingId) {
        await axios.put(`/api/apis/${editingId}`, apiData);
        setSuccess('API updated successfully');
        setEditingId(null);
      } else {
        await axios.post('/api/apis', apiData);
        setSuccess('API created successfully');
      }

      setFormData({
        name: '',
        description: '',
        url: '',
        method: 'GET',
        headers: '{"Content-Type": "application/json"}',
        bodyMode: 'raw',
        body: '',
        formdata: [],
        expectedStatus: 200
      });
      setShowForm(false);
      fetchApis();
    } catch (error) {
      const message = error.response?.data?.error || error.message;
      setError('Error saving API: ' + message);
    }
  };

  const handleEdit = (api) => {
    setFormData({
      name: api.name,
      description: api.description,
      url: api.url,
      method: api.method,
      headers: JSON.stringify(api.headers, null, 2),
      bodyMode: (api.body && typeof api.body === 'object' && api.body.mode === 'formdata') ? 'formdata' : 'raw',
      body: (api.body && typeof api.body === 'object' && api.body.mode === 'formdata') ? '' : (api.body || ''),
      formdata: (api.body && typeof api.body === 'object' && api.body.mode === 'formdata') ? api.body.formdata : [],
      expectedStatus: api.expectedStatus || 200
    });
    setEditingId(api.id);
    setShowForm(true);
  };

  const openDeleteModal = (api) => {
    setError('');
    setSuccess('');
    setApiToDelete(api);
  };

  const closeDeleteModal = () => {
    if (deletingApi) return;
    setApiToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!apiToDelete) return;

    setDeletingApi(true);
    setError('');

    try {
      await axios.delete(`/api/apis/${apiToDelete.id}`);
      setSuccess(`API "${apiToDelete.name}" deleted successfully`);
      setApiToDelete(null);
      fetchApis();
    } catch (error) {
      setError('Failed to delete API: ' + error.message);
    } finally {
      setDeletingApi(false);
    }
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const closeImportModal = (force = false) => {
    if (importing && !force) return;
    setShowImportModal(false);
    setSelectedPostmanFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePostmanFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setSelectedPostmanFile(file);
  };

  const handleImportPostmanCollection = async () => {
    if (!selectedPostmanFile) {
      setError('Please choose a Postman collection file first.');
      return;
    }

    setError('');
    setSuccess('');
    setImporting(true);

    try {
      const fileContent = await selectedPostmanFile.text();
      let collection;
      try {
        collection = JSON.parse(fileContent);
      } catch (parseError) {
        setError('Invalid JSON file. Please upload a valid Postman collection JSON.');
        return;
      }

      const response = await axios.post('/api/apis/import-postman', { collection });
      const {
        importedCount,
        duplicateCount = 0,
        invalidCount = 0,
        totalInCollection
      } = response.data || {};

      const skippedDetails = [];
      if (duplicateCount) skippedDetails.push(`${duplicateCount} duplicate`);
      if (invalidCount) skippedDetails.push(`${invalidCount} invalid`);
      const skippedText = skippedDetails.length ? `, skipped ${skippedDetails.join(' and ')}` : '';

      setSuccess(`Imported ${importedCount || 0} APIs from Postman collection${skippedText} (total requests: ${totalInCollection || 0}).`);
      fetchApis();
      closeImportModal(true);
    } catch (uploadError) {
      const message = uploadError.response?.data?.error || uploadError.message;
      setError('Failed to import Postman collection: ' + message);
    } finally {
      setImporting(false);
    }
  };

  const filteredApis = apis.filter(api => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;

    const searchableText = [
      api.name || '',
      api.description || '',
      api.method || '',
      api.url || ''
    ]
      .join(' ')
      .toLowerCase();

    return searchableText.includes(query);
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h1 style={{ margin: 0 }}>API Collection</h1>
        {showSearch ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'min(460px, 100%)' }}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, method, URL, or description"
              style={{ minWidth: '220px' }}
            />
            <button
              type="button"
              className="icon-btn"
              onClick={() => setShowSearch(false)}
              aria-label="Close search"
              title="Close search"
            >
              x
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="icon-btn"
            onClick={() => setShowSearch(true)}
            aria-label="Show search"
            title="Search"
          >
            <MdSearch />
          </button>
        )}
      </div>

      <APIQuickStartGuide />

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="action-buttons" style={{ marginBottom: '15px' }}>
        <button className="btn btn-primary" onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({
            name: '',
            description: '',
            url: '',
            method: 'GET',
            headers: '{"Content-Type": "application/json"}',
            bodyMode: 'raw',
            body: '',
            formdata: [],
            expectedStatus: 200
          });
        }}>
          {showForm ? 'Cancel' : 'Add New API'}
        </button>

        <button
          className="btn btn-secondary"
          onClick={() => {
            setError('');
            setSuccess('');
            setSelectedPostmanFile(null);
            setShowImportModal(true);
          }}
          disabled={importing}
        >
          {importing ? 'Importing...' : 'Upload Postman Collection'}
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handlePostmanFileChange}
          style={{ display: 'none' }}
        />
      </div>

      {showImportModal && (
        <div className="modal-overlay" onClick={closeImportModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Postman Collection</h2>
              <button className="modal-close" onClick={closeImportModal} disabled={importing}>
                x
              </button>
            </div>
            <div className="modal-body">
              <p>Select a Postman collection JSON file to import APIs into this list.</p>
              {host && (
                <p>
                  Current HOST: <code>{host}</code>
                </p>
              )}
              <div className="file-pill">
                {selectedPostmanFile ? selectedPostmanFile.name : 'No file selected'}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={openFilePicker} disabled={importing}>
                Choose File
              </button>
              <button
                className="btn btn-success"
                onClick={handleImportPostmanCollection}
                disabled={importing || !selectedPostmanFile}
              >
                {importing ? 'Importing...' : 'Import Collection'}
              </button>
              <button className="btn btn-primary" onClick={closeImportModal} disabled={importing}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {apiToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content modal-content-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete API</h2>
              <button className="modal-close" onClick={closeDeleteModal} disabled={deletingApi}>
                x
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this API?</p>
              <div className="file-pill">
                <strong>{apiToDelete.name}</strong>
              </div>
              <p style={{ marginTop: '10px', color: '#d6336c' }}>
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={closeDeleteModal} disabled={deletingApi}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleConfirmDelete} disabled={deletingApi}>
                {deletingApi ? 'Deleting...' : 'Delete API'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="card">
          <h2>{editingId ? 'Edit API Test' : 'Create New API Test'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Test Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Get User Data"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Test description"
                rows="2"
              />
            </div>

            <div className="form-group">
              <label>API URL *</label>
              {host && (
                <div style={{ 
                  marginBottom: '8px', 
                  padding: '8px', 
                  backgroundColor: 'var(--bg-input)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-muted)',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}>
                  Info: <strong style={{ color: 'var(--text-main)' }}>HOST configured:</strong>{' '}
                  <code style={{ background: 'var(--bg-panel-soft)', color: 'var(--text-main)', padding: '1px 4px', borderRadius: '3px' }}>
                    {host}
                  </code>
                  <br/>
                  Relative paths are auto-prefixed with HOST. Example:{' '}
                  <code style={{ background: 'var(--bg-panel-soft)', color: 'var(--text-main)', padding: '1px 4px', borderRadius: '3px' }}>
                    /v1/accounts/users/login
                  </code>
                </div>
              )}
              <input
                type="text"
                name="url"
                value={formData.url}
                onChange={handleChange}
                placeholder={host ? "e.g., /v1/users/{{userId}}" : "e.g., https://api.example.com/users/{{userId}}"}
                required
              />
              <small>Use relative URL with HOST, or full absolute URL. Use {'{{columnName}}'} for CSV placeholders.</small>
            </div>

            <div className="form-group">
              <label>HTTP Method *</label>
              <select name="method" value={formData.method} onChange={handleChange}>
                <option>GET</option>
                <option>POST</option>
                <option>PUT</option>
                <option>DELETE</option>
                <option>PATCH</option>
              </select>
            </div>

            <div className="form-group">
              <label>Headers (JSON)</label>
              <textarea
                name="headers"
                value={formData.headers}
                onChange={handleChange}
                placeholder='{"Content-Type": "application/json"}'
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Request Body (optional)</label>
              
              <div style={{ marginBottom: '12px', display: 'flex', gap: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontWeight: 500, color: formData.bodyMode === 'raw' ? 'var(--primary)' : 'inherit' }}>
                  <input
                    type="radio"
                    checked={formData.bodyMode === 'raw'}
                    onChange={() => {
                      setFormData(prev => ({ ...prev, bodyMode: 'raw' }));
                      // If headers contain multipart, and user switches to raw, maybe update it?
                    }}
                  />
                  JSON (Raw)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontWeight: 500, color: formData.bodyMode === 'formdata' ? 'var(--primary)' : 'inherit' }}>
                  <input
                    type="radio"
                    checked={formData.bodyMode === 'formdata'}
                    onChange={() => {
                      setFormData(prev => {
                        const next = { ...prev, bodyMode: 'formdata' };
                        try {
                          let h = JSON.parse(prev.headers || '{}');
                          delete h['Content-Type'];
                          next.headers = JSON.stringify(h, null, 2);
                        } catch(e) {}
                        return next;
                      });
                    }}

                  />
                  Multipart Form Data
                </label>
              </div>

              {formData.bodyMode === 'raw' ? (
                <textarea
                  name="body"
                  value={formData.body}
                  onChange={handleChange}
                  placeholder='{"name": "{{name}}", "email": "{{email}}"}'
                  rows="4"
                  style={{ fontFamily: 'monospace' }}
                />
              ) : (
                <div className="formdata-editor" style={{ background: 'var(--bg-input)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) minmax(180px, 1fr) 100px 40px', gap: '8px', marginBottom: '8px', fontWeight: 600, fontSize: '13px' }}>
                    <div>Key</div>
                    <div>Value</div>
                    <div>Type</div>
                    <div></div>
                  </div>
                  {formData.formdata.map((item, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) minmax(180px, 1fr) 100px 40px', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="text"
                        value={item.key}
                        onChange={(e) => {
                          const newList = [...formData.formdata];
                          newList[idx].key = e.target.value;
                          setFormData(prev => ({ ...prev, formdata: newList }));
                        }}
                        placeholder="Key"
                        style={{ padding: '4px 8px' }}
                      />
                      <input
                        type="text"
                        value={item.value}
                        onChange={(e) => {
                          const newList = [...formData.formdata];
                          newList[idx].value = e.target.value;
                          setFormData(prev => ({ ...prev, formdata: newList }));
                        }}
                        placeholder={item.type === 'file' ? 'filename.jpg (in data/uploads)' : 'Value or {{placeholder}}'}
                        style={{ padding: '4px 8px' }}
                      />
                      <select
                        value={item.type || 'text'}
                        onChange={(e) => {
                          const newList = [...formData.formdata];
                          newList[idx].type = e.target.value;
                          setFormData(prev => ({ ...prev, formdata: newList }));
                        }}
                        style={{ padding: '4px 8px' }}
                      >
                        <option value="text">Text</option>
                        <option value="file">File</option>
                      </select>
                      <button
                        type="button"
                        className="btn btn-danger btn-icon-sq"
                        style={{ fontSize: '15px' }}
                        onClick={() => {
                          setFormData(prev => ({ ...prev, formdata: prev.formdata.filter((_, i) => i !== idx) }));
                        }}
                        title="Delete Field"
                      >
                        <MdDelete />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ marginTop: '8px', padding: '4px 12px', fontSize: '12px' }}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, formdata: [...prev.formdata, { key: '', value: '', type: 'text', disabled: false }] }));
                    }}
                  >
                    + Add Field
                  </button>
                </div>
              )}
              <small style={{ display: 'block', marginTop: '6px' }}>Use {'{{columnName}}'} for CSV column placeholders. Files must be located in <code>data/uploads/</code></small>
            </div>


            <div className="form-group">
              <label>Expected Status Code *</label>
              <input
                type="number"
                name="expectedStatus"
                value={formData.expectedStatus}
                onChange={handleChange}
                placeholder="200"
                required
              />
              <small>The HTTP status code you expect for a successful test (e.g., 200, 201, 400).</small>
            </div>

            <button type="submit" className="btn btn-success">
              {editingId ? 'Update API' : 'Create API'}
            </button>
          </form>
        </div>
      )}

      <h2 style={{ marginTop: '30px' }}>Created API Tests ({filteredApis.length})</h2>
      {apis.length === 0 ? (
        <div className="card">
          <p>No API tests created yet. Create one to get started!</p>
        </div>
      ) : filteredApis.length === 0 ? (
        <div className="card">
          <p>No APIs found for "<strong>{searchTerm}</strong>". Try a different search.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Method</th>
                <th>URL</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApis.map(api => (
                <tr key={api.id}>
                  <td><strong>{api.name}</strong></td>
                  <td><span className="badge badge-warning">{api.method}</span></td>
                  <td><small>{api.url.substring(0, 50)}...</small></td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-primary btn-icon-sq"
                        onClick={() => handleEdit(api)}
                        title="Edit"
                      >
                        <MdEdit />
                      </button>
                      <button
                        className="btn btn-danger btn-icon-sq"
                        onClick={() => openDeleteModal(api)}
                        title="Delete"
                      >
                        <MdDelete />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default APIManagement;
